import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from '../hooks/useSoundEffects';
import { fetchLocalizedTitleNameById } from '../utils/battleTitleLocalization';
import { API_URL } from '../config/apiConfig';
import { resolvePublicAssetBackgroundUrl } from '../utils/resolvePublicAssetUrl';
import './AchievementUnlockedToast.css';

const TITLE_COLORS = {
  white: '#e0e0e0',
  blue: '#5eb3f6',
  purple: '#b388ff',
  golden: '#ffd700'
};

/**
 * Shows "Achievement unlocked: [Title Name]" and why (e.g. "Winning a battle without taking damage").
 * Pass titleId so the title is resolved from /profile/titles for the current UI language.
 * titleName is the server default (usually English) and is only used when titleId is absent or localization fails.
 *
 * @param {object} props
 * @param {number|string|undefined|null} [props.titleId]
 * @param {string|undefined|null} props.titleName
 * @param {string} [props.color]
 * @param {string} [props.achievementKey]
 * @param {() => void} [props.onClose]
 * @param {number} [props.duration]
 */
const AchievementUnlockedToast = ({
  titleId,
  titleName,
  color = 'golden',
  achievementKey,
  onClose,
  duration = 10000
}) => {
  const { t, i18n } = useTranslation();
  const [displayTitle, setDisplayTitle] = useState(null);
  const [ready, setReady] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const applyEnglishFallback = (msg, err) => {
      console.error(msg, err);
      if (!titleName || String(titleName).trim() === '') {
        throw err instanceof Error ? err : new Error(String(msg));
      }
      if (!cancelled) {
        setDisplayTitle(String(titleName));
        setReady(true);
      }
    };

    void (async () => {
      if (titleId == null || titleId === '') {
        if (!titleName || String(titleName).trim() === '') {
          throw new Error('AchievementUnlockedToast: titleName is required when titleId is not provided');
        }
        if (!cancelled) {
          setDisplayTitle(String(titleName));
          setReady(true);
        }
        return;
      }
      const token = localStorage.getItem('token');
      if (!token) {
        applyEnglishFallback('AchievementUnlockedToast: no token; cannot load localized title', new Error('missing token'));
        return;
      }
      try {
        const name = await fetchLocalizedTitleNameById(API_URL, token, titleId, i18n.language);
        if (!cancelled) {
          setDisplayTitle(name);
          setReady(true);
        }
      } catch (err) {
        applyEnglishFallback('AchievementUnlockedToast: failed to load localized title', err);
      }
    })().catch((err) => {
      console.error('AchievementUnlockedToast:', err);
    });

    return () => {
      cancelled = true;
    };
  }, [titleId, titleName, i18n.language]);

  useEffect(() => {
    if (!ready || !displayTitle) return undefined;
    if (typeof playSoundEffect === 'function') {
      playSoundEffect('/sounds/achievement_unlocked.mp3', 0.6);
    }
    const timer = setTimeout(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setIsVisible(false);
        if (onClose) onClose();
      }, 300);
    }, duration);
    return () => clearTimeout(timer);
  }, [ready, displayTitle, duration, onClose]);

  const handleClose = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, 300);
  };

  if (!isVisible || !ready || !displayTitle) return null;

  const titleColor = TITLE_COLORS[color] || TITLE_COLORS.golden;
  const reasonKey = achievementKey ? `achievementUnlocked.reason.${achievementKey}` : null;
  const reasonText = reasonKey ? t(reasonKey) : null;
  const showReason = reasonText && reasonText !== reasonKey;
  const bgImage = resolvePublicAssetBackgroundUrl('/images/achievement_unlocked.jpg');

  return createPortal(
    <div
      className={`achievement-unlocked-toast ${isAnimating ? 'achievement-unlocked-toast-exit' : ''}`}
      onClick={handleClose}
      role="alert"
      style={{
        backgroundImage: bgImage,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="achievement-unlocked-toast-content">
        <span className="achievement-unlocked-toast-label">{t('achievementUnlocked.label')}</span>
        <span className="achievement-unlocked-toast-title" style={{ color: titleColor }}>{displayTitle}</span>
        {showReason && <span className="achievement-unlocked-toast-reason">{reasonText}</span>}
      </div>
      <button
        type="button"
        className="achievement-unlocked-toast-close"
        onClick={(e) => { e.stopPropagation(); handleClose(); }}
        aria-label={t('common.close')}
      >
        ×
      </button>
    </div>,
    document.body
  );
};

export default AchievementUnlockedToast;
